import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";

interface EmailTestSet {
  id: string;
  name: string;
  description: string | null;
  emailCount: number;
}

interface EmailAdminActionsProps {
  emailId?: string;
  messageId?: string;
  inboxEmail?: string;
  sharedInbox?: string;
}

/**
 * Admin actions for emails - dropdown with various admin operations
 */
export const EmailAdminActions: React.FC<EmailAdminActionsProps> = ({
  emailId,
  messageId,
  inboxEmail,
  sharedInbox,
}) => {
  const [selectedAction, setSelectedAction] = useState(
    emailId ? "copy-test-fixture" : "store-email-s3"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [storeEmailDialogOpen, setStoreEmailDialogOpen] = useState(false);
  const [storedEmailId, setStoredEmailId] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();

  // Test set state
  const [testSetDialogOpen, setTestSetDialogOpen] = useState(false);
  const [testSets, setTestSets] = useState<EmailTestSet[]>([]);
  const [testSetsLoading, setTestSetsLoading] = useState(false);
  const [selectedTestSetId, setSelectedTestSetId] = useState<string>("");
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newTestSetName, setNewTestSetName] = useState("");
  const [newTestSetDescription, setNewTestSetDescription] = useState("");

  const handleActionChange = (event: any) => {
    const action = event.target.value;
    setSelectedAction(action);
  };

  const loadTestSets = async () => {
    setTestSetsLoading(true);
    try {
      const response = await apiClient.post<{ testSets: EmailTestSet[] }>(
        "/admin/email-test-sets/list",
        {},
        { withCredentials: true }
      );
      setTestSets(response.testSets);
    } catch (error) {
      console.error("Failed to load test sets:", error);
      showSnackbar("Failed to load test sets", 3000, "error");
    } finally {
      setTestSetsLoading(false);
    }
  };

  const handleOpenTestSetDialog = () => {
    setTestSetDialogOpen(true);
    setSelectedTestSetId("");
    setIsCreatingNew(false);
    setNewTestSetName("");
    setNewTestSetDescription("");
    loadTestSets();
  };

  const handleCloseTestSetDialog = () => {
    setTestSetDialogOpen(false);
    setSelectedTestSetId("");
    setIsCreatingNew(false);
    setNewTestSetName("");
    setNewTestSetDescription("");
  };

  /**
   * Ensures we have a database email ID. If emailId is provided, returns it.
   * Otherwise, stores the email first using messageId + inboxEmail.
   * Returns the database email ID or null if unable to get one.
   */
  const ensureEmailStored = async (): Promise<string | null> => {
    // If we already have a database email ID, return it
    if (emailId) {
      return emailId;
    }

    // Need messageId + inboxEmail to store the email
    if (!messageId || !inboxEmail) {
      showSnackbar(
        "Email ID or Message ID with inbox email is required",
        3000,
        "error"
      );
      return null;
    }

    // Store the email first
    showSnackbar("Storing email first...", 2000, "info");
    const storeResponse = await apiClient.post<{
      success: boolean;
      emailId?: string;
      error?: string;
    }>(
      "/admin/store_email_in_s3",
      { userEmail: inboxEmail, messageId, sharedInbox },
      { withCredentials: true }
    );

    if (!storeResponse.success || !storeResponse.emailId) {
      throw new Error(storeResponse.error || "Failed to store email");
    }

    showSnackbar("Email stored successfully!", 2000, "success");
    return storeResponse.emailId;
  };

  const handleAddToTestSet = async () => {
    setIsLoading(true);
    try {
      const actualEmailId = await ensureEmailStored();
      if (!actualEmailId) {
        setIsLoading(false);
        return;
      }

      let targetTestSetId = selectedTestSetId;

      // Create new test set if needed
      if (isCreatingNew) {
        if (!newTestSetName.trim()) {
          showSnackbar("Test set name is required", 3000, "error");
          setIsLoading(false);
          return;
        }

        const createResponse = await apiClient.post<{
          testSet: { id: string; name: string };
        }>(
          "/admin/email-test-sets/create",
          {
            name: newTestSetName.trim(),
            description: newTestSetDescription.trim() || undefined,
          },
          { withCredentials: true }
        );
        targetTestSetId = createResponse.testSet.id;
        showSnackbar(
          `Created test set: ${createResponse.testSet.name}`,
          2000,
          "info"
        );
      }

      if (!targetTestSetId) {
        showSnackbar("Please select or create a test set", 3000, "error");
        setIsLoading(false);
        return;
      }

      // Add email to test set
      await apiClient.post(
        "/admin/email-test-sets/add-email",
        {
          testSetId: targetTestSetId,
          emailId: actualEmailId,
        },
        { withCredentials: true }
      );

      showSnackbar("Email added to test set!", 3000, "success");
      handleCloseTestSetDialog();
    } catch (error: any) {
      console.error("Failed to add email to test set:", error);
      const errorMessage =
        error?.response?.data?.error || "Failed to add email to test set";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecuteAction = async () => {
    if (!selectedAction) return;

    if (selectedAction === "copy-test-fixture") {
      await handleCopyTestFixture();
    } else if (selectedAction === "store-email-s3") {
      await handleStoreEmailInS3();
    } else if (selectedAction === "add-to-test-set") {
      handleOpenTestSetDialog();
      return; // Don't reset dropdown for dialog actions
    }

    // Reset dropdown after action
    setSelectedAction("");
  };

  const handleCopyTestFixture = async () => {
    setIsLoading(true);
    try {
      const actualEmailId = await ensureEmailStored();
      if (!actualEmailId) {
        setIsLoading(false);
        return;
      }

      const response = await apiClient.post<{ fixture: string }>(
        "/documents/email/test/generate-test-fixture",
        { emailId: actualEmailId },
        { withCredentials: true }
      );

      // Copy to clipboard
      await navigator.clipboard.writeText(response.fixture);

      showSnackbar("Test email fixture copied to clipboard!", 3000, "info");
    } catch (error: any) {
      console.error("Failed to copy test fixture:", error);
      const errorMessage =
        error?.response?.data?.error || "Failed to generate test fixture";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStoreEmailInS3 = async () => {
    if (!messageId || !inboxEmail) {
      showSnackbar(
        "Message ID and inbox email are required for this action",
        3000,
        "error"
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post<{
        success: boolean;
        emailId?: string;
        error?: string;
      }>(
        "/admin/store_email_in_s3",
        { userEmail: inboxEmail, messageId, sharedInbox },
        { withCredentials: true }
      );

      if (response.success && response.emailId) {
        setStoredEmailId(response.emailId);
        setStoreEmailDialogOpen(true);
        showSnackbar("Email stored in S3 successfully!", 3000, "success");
      } else {
        throw new Error(response.error || "Failed to store email");
      }
    } catch (error: any) {
      console.error("Failed to store email in S3:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error.message ||
        "Failed to store email in S3";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyEmailIdToClipboard = async () => {
    if (storedEmailId) {
      try {
        await navigator.clipboard.writeText(storedEmailId);
        showSnackbar("Email ID copied to clipboard!", 2000, "info");
      } catch (err) {
        console.error("Failed to copy:", err);
        showSnackbar("Failed to copy to clipboard", 2000, "error");
      }
    }
  };

  return (
    <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 200 }} disabled={isLoading}>
        <InputLabel id="admin-action-label">Admin Actions</InputLabel>
        <Select
          labelId="admin-action-label"
          id="admin-action-select"
          value={selectedAction}
          label="Admin Actions"
          onChange={handleActionChange}
        >
          {(emailId || (messageId && inboxEmail)) && (
            <MenuItem value="copy-test-fixture">
              <Typography variant="body2">Copy test email fixture</Typography>
            </MenuItem>
          )}
          {messageId && (
            <MenuItem value="store-email-s3">
              <Typography variant="body2">Store email in S3</Typography>
            </MenuItem>
          )}
          {(emailId || (messageId && inboxEmail)) && (
            <MenuItem value="add-to-test-set">
              <Typography variant="body2">Add to Test Set</Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        size="small"
        onClick={handleExecuteAction}
        disabled={!selectedAction || isLoading}
      >
        Execute
      </Button>

      {/* Dialog to show stored email ID */}
      <Dialog
        open={storeEmailDialogOpen}
        onClose={() => setStoreEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Email Stored Successfully
          <IconButton
            aria-label="close"
            onClick={() => setStoreEmailDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              Email ID:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "monospace",
                backgroundColor: "#f5f5f5",
                padding: "4px 8px",
                borderRadius: "4px",
                flexGrow: 1,
              }}
            >
              {storedEmailId}
            </Typography>
            <IconButton
              size="small"
              onClick={copyEmailIdToClipboard}
              title="Copy Email ID"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            The email has been successfully stored in S3. Use this email ID to
            access the stored email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStoreEmailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for adding email to test set */}
      <Dialog
        open={testSetDialogOpen}
        onClose={handleCloseTestSetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Email to Test Set
          <IconButton
            aria-label="close"
            onClick={handleCloseTestSetDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {testSetsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {!isCreatingNew && (
                <>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Select Test Set</InputLabel>
                    <Select
                      value={selectedTestSetId}
                      label="Select Test Set"
                      onChange={(e) => setSelectedTestSetId(e.target.value)}
                    >
                      {testSets.map((ts) => (
                        <MenuItem key={ts.id} value={ts.id}>
                          {ts.name} ({ts.emailCount} emails)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      OR
                    </Typography>
                  </Divider>

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setIsCreatingNew(true)}
                    fullWidth
                  >
                    Create New Test Set
                  </Button>
                </>
              )}

              {isCreatingNew && (
                <>
                  <TextField
                    fullWidth
                    label="Test Set Name"
                    value={newTestSetName}
                    onChange={(e) => setNewTestSetName(e.target.value)}
                    sx={{ mt: 1 }}
                    autoFocus
                  />
                  <TextField
                    fullWidth
                    label="Description (optional)"
                    value={newTestSetDescription}
                    onChange={(e) => setNewTestSetDescription(e.target.value)}
                    sx={{ mt: 2 }}
                    multiline
                    rows={2}
                  />
                  <Button
                    variant="text"
                    onClick={() => {
                      setIsCreatingNew(false);
                      setNewTestSetName("");
                      setNewTestSetDescription("");
                    }}
                    sx={{ mt: 1 }}
                  >
                    Cancel - Select Existing Instead
                  </Button>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestSetDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddToTestSet}
            disabled={
              isLoading ||
              (!isCreatingNew && !selectedTestSetId) ||
              (isCreatingNew && !newTestSetName.trim())
            }
          >
            {isLoading ? <CircularProgress size={20} /> : "Add to Test Set"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
